import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  activeSchema,
  BondStatuses,
  getBondInfo,
  schema,
  Step,
} from "./components/forms/FormBondApp.constants";
import { useBondRecordData } from "./hooks/useBondRecordData";
import { useShippingOption } from "./hooks/useShippingOption";

import {
  getBondRequestByStep,
  getForms,
  getPdfUrl,
  getSchema,
  getSteps,
  serializeRecoveryData,
} from "./components/forms/FormBondApp.helpers";
import BondAppFormContext from "./contexts/BondAppFormContext";
import { useBondRecords } from "./hooks/useBondRecords";
import { useShippingMethods } from "./hooks/useShippingMethods";
import { useIssuances } from "./hooks/useIssuances";
import { useScroll } from "./hooks/useScroll";
import {
  CustomCheckbox,
  CustomSelect,
  CustomDatePicker,
  SingleCheckboxWidget,
  CurrencyWidget,
} from "./components/forms/FormBondApp.fields";
import ShippingService from "./api/services/ShippingService";
import { ExternalProduct } from "./api/services/ProductsService";
import { stepWidthMapping } from "./components/StepWide/StepWide";

import partners from "./constants/partners.json";
import { partnerId } from "./api/core";
import {
  BondInfo,
  BondRecordResponse,
  FormItem,
  StepItem,
  FormData,
  WorkflowStatus,
  SidebarData,
} from "./components/forms/FormBondApp.types";
import { FormContextType } from "./FormContext.types";
import { usePortalSiteInfo } from "./hooks/usePortalSiteInfo";
import {
  getExternalBondInfo,
  getExternalBondRecordPayload,
  getExternalPremiumInCents,
  getExternalSchemaForStep,
  getExternalStepHeader,
  getExternalSteps,
} from "./components/forms/FormBondApp.external";
import { useExternalBondRecord } from "./hooks/useExternalBondRecord";

export const FormContext = React.createContext<FormContextType | undefined>(
  undefined,
);

export const useFormContext = () => {
  const context = React.useContext(FormContext);
  if (context === undefined) {
    throw new Error("useFormContext must be used within a FormContextProvider");
  }
  return context;
};

export const FormContextProvider: React.FC<any> = ({ children }) => {
  const context = useContext(BondAppFormContext);
  const product = context.state.product;
  const hasInstructions = product?.application_instructions;
  const isBondLoading: boolean = context.state.isBondLoading;
  const isBondError: boolean = context.state.isBondError;

  // an external product is underwritten, priced and issued by the integration,
  // so it takes a reduced flow with no rating, credit, payment or shipping
  const externalProduct: ExternalProduct | null =
    product?.external_product ?? null;
  const isExternalProduct = Boolean(externalProduct);
  const hasExternalFormSpecificFields = Boolean(
    externalProduct?.form_specific_fields?.length,
  );

  const [step, setStep] = useState<Step>(
    hasInstructions ? Step.INSTRUCTIONS : Step.START,
  );
  const [formDataStart, setFormDataStart] = React.useState<FormData>(null);
  const [formDataBondApplicant, setFormDataBondApplicant] = React.useState<
    FormData
  >(null);
  const [formDataBondBusiness, setFormDataBondBusiness] = React.useState<
    FormData
  >(null);
  const [formDataBondRisk, setFormDataBondRisk] = React.useState<FormData>(
    null,
  );
  const [formDataBondSpecific, setFormDataBondSpecific] = React.useState<
    FormData
  >(null);
  const [formDataBondIndemnitor, setFormDataBondIndemnitor] = React.useState<
    FormData
  >(null);
  const [formDataBondOptions, setFormDataBondOptions] = React.useState<
    FormData
  >(null);
  const [checkboxesApprove, setCheckboxesApprove] = useState<number[]>([]);
  const [formDataBondAgencyQuestions, setFormDataBondAgencyQuestions] = useState<FormData>(null)
  const [
    formDataExternalFormSpecific,
    setFormDataExternalFormSpecific,
  ] = useState<FormData>(null);
  const [
    formDataExternalBondOptions,
    setFormDataExternalBondOptions,
  ] = useState<FormData>(null);

  const [amountOfIndemnitor, setAmountOfIndemnitor] = React.useState<number>(1);
  const [isBusiness, setIsBusiness] = useState<boolean>();
  const [bondRecord, setBondRecord] = useState<BondRecordResponse>();
  const [error, setError] = useState<any>();
  const [refCode, setRefCode] = useState("");
  const [isNeedToCheckIndemnitor, setNeedToCheckIndemnitor] = useState(false);
  const [isFirstFetchRecordLoading, setFirstFetchRecordLoading] = useState(
    false,
  );
  const [isShowError, setShowError] = useState(false);
  const [isFirstLoading, setFirstLoading] = useState(true);
  const [isShippingLoading, setIsShippingLoading] = useState(false);
  const [bondStatus, setBondStatus] = useState<BondStatuses>();
  const [pdfElectronicBondFileId, setPdfElectronicBondFileId] = useState<
    string | null
  >();
  const [isErrorSaveShipping, setIsErrorSaveShipping] = useState(false);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const queryRecordId = searchParams.get("recordId");
  const queryAccessKey = searchParams.get("accessKey");
  const bondId = searchParams.get("id");
  const queryPaymentStatus = searchParams.get("payment_status");

  const navigate = useNavigate();

  //GET BondRecords

  const {
    data: bondRecordRecovery,
    error: bondRecordRecoveryError,
    isError: bondRecordRecoveryIsError,
    isLoading: bondRecordRecoveryIsLoading,
    isRefetching: bondRecordRecoveryIsRefetching,
    refetch: bondRecordRecoveryRefetch,
  } = useBondRecordData(queryRecordId, queryAccessKey, queryPaymentStatus);

  const {
    data: shippingOptions,
    refetch: fetchShippingOptions,
  } = useShippingOption(
    "shippingOptions",
    queryRecordId ? queryRecordId : "",
    queryAccessKey ? queryAccessKey : "",
  );

  const {
    issuancesRecordData,
    issuancesIsFetching,
    fetchIssuances,
  } = useIssuances(
    bondRecord?.data?.record_salesforce_id || queryRecordId || "",
    queryAccessKey || "",
  );

  const bondRecoveryData = bondRecordRecovery;
  const bondRecordCurrentData = bondRecord?.data?.record;

  const {
    shippingMethods,
    isShippingMethodsLoading,
    shippingMethodsError,
    fetchShippingMethods,
  } = useShippingMethods(
    queryRecordId
  );

  const {
    submitExternalBondRecord,
    isExternalSubmitLoading,
    externalSubmitErrorMessage,
  } = useExternalBondRecord();

  const currentBondRecordData =
    issuancesRecordData || bondRecord?.data?.record || bondRecordRecovery;

  const appRef = useRef<HTMLDivElement>(null);

  const isOnStart = isBusiness === undefined;

  const isDisableAutoScroll =
    (!hasInstructions && (isOnStart || step === Step.START)) ||
    step === Step.INSTRUCTIONS ||
    step === Step.BOND_APPLICANT ||
    step === Step.BOND_BUSINESS;

  useScroll(appRef, step, isDisableAutoScroll);

  useEffect(() => {
    if (!bondId && !window.location.href.indexOf("/404")) {
      window.location.href = "/404";
    }

    if (
      bondRecoveryData?.product_id &&
      bondId !== bondRecoveryData?.product_id
    ) {
      localStorage.removeItem("principalEmail");
    }
  }, [bondId]);

  useEffect(() => {
    if (queryPaymentStatus?.toUpperCase() === "SUCCEEDED") {
      if (bondId && bondId.split("_").length > 1 && bondId.includes("_")) {
        const bondParams = bondId.split("_");
        if (Array.isArray(bondParams)) {
          const [productId, recordId, accessKey] = bondParams;
          const paramsString = `?id=${productId}&recordId=${recordId}&accessKey=${accessKey}&payment_status=${queryPaymentStatus}`;
          navigate(paramsString);
        }
      }

      if (queryRecordId && queryAccessKey) {
        fetchShippingOptions();
      }
      setStep(Step.COMPLETE);
    }
  }, [queryPaymentStatus]);

  const bondRequestByStep: any = getBondRequestByStep({
    searchParams,
    bondRecordCurrentData,
    refCode,
    isBusiness,
    bondRecord,
    formDataBondApplicant,
    formDataBondBusiness,
    formDataBondRisk,
    formDataBondSpecific,
    formDataBondIndemnitor,
    formDataBondOptions,
    formDataBondAgencyQuestions,
    product,
  });

  const { data: portalSiteInfo } = usePortalSiteInfo();

  const {
    data: bondRecords,
    refetch,
    isError,
    isLoading,
    isRefetching,
  } = useBondRecords(
    bondRequestByStep?.[step],
    (data: BondRecordResponse) => {
      setBondRecord(data);
      setError(undefined);

      if (
        step === Step.BOND_INDEMNITOR &&
        !data?.data?.record?.is_credit_required
      ) {
        setStep(Step.BOND_OPTIONS);
      }

      if (
        !queryAccessKey ||
        data.data.access_key !== queryAccessKey ||
        !queryRecordId ||
        queryRecordId !== data.data.record_salesforce_id
      ) {
        navigate(
          `?id=${product?.id}&recordId=${data.data.record_salesforce_id}&accessKey=${data.data.access_key}`,
        );
      }
    },
    (data: any) => {
      data?.request?.response &&
        setError(JSON.parse(data?.request?.response)?.error);
    },
    bondRecord?.data?.access_key || "",
  );

  useEffect(() => {
    setRefCode("EZ-" + Math.random().toString(36).substring(4).toUpperCase());
  }, []);

  const paymentLink = currentBondRecordData?.salesforce_payment_link
    ? new URL(currentBondRecordData?.salesforce_payment_link)
    : "";

  if (typeof paymentLink === "object") {
    const redirectLink = new URL(window.location.href);
    redirectLink.searchParams.set(
      "id",
      `${product?.id || bondId}_${currentBondRecordData?.id}_${
        queryAccessKey || bondRecord?.data?.access_key
      }`,
    );
    redirectLink.searchParams.set("payment", "1");
    paymentLink.searchParams.set("theme", "1");
    if (bondRecordCurrentData?.principal_email) {
      paymentLink.searchParams.set(
        "email",
        bondRecordCurrentData?.principal_email,
      );
    }
    paymentLink.searchParams.set("hideShipping", "1");
    paymentLink.searchParams.set("redirect", redirectLink.href);
  }

  useEffect(() => {
    if (
      (bondRecordCurrentData?.status_name ===
        BondStatuses.UNDERWRITING_REVIEW ||
        bondRecoveryData?.status_name === BondStatuses.UNDERWRITING_REVIEW) &&
      queryAccessKey &&
      queryRecordId
    ) {
      setTimeout(() => {
        bondRecordRecoveryRefetch();
      }, 30000);
    }
  }, [bondRecord]);

  useEffect(() => {
    if (
      bondRecordRecovery?.status_name !== BondStatuses.BOND_ISSUE_READY &&
      bondStatus &&
      step !== Step.PROCESSING
    ) {
      localStorage.removeItem("bondStatus");
      localStorage.removeItem("pdfElectronicBondFileId");
    }

    if (
      bondRecoveryData?.status_name === BondStatuses.BOND_ISSUE_READY &&
      !issuancesRecordData
    ) {
      setBondStatus(bondRecoveryData?.status_name);
      fetchIssuances().then(({ data }) => {
        if (data?.data?.record?.workflow_status === WorkflowStatus.COMPLETE) {
          setStep(Step.COMPLETE);
        }
      });
    }

    if (
      bondRecoveryData?.status_name === BondStatuses.UNDERWRITING_REVIEW &&
      queryAccessKey &&
      queryRecordId
    ) {
      setTimeout(() => {
        bondRecordRecoveryRefetch();
      }, 30000);
    }

    setBondStatus(
      bondRecoveryData?.status_name || issuancesRecordData?.status_name,
    );
    setPdfElectronicBondFileId(
      issuancesRecordData?.pdf_electronic_bond_file_id,
    );
  }, [bondRecordRecovery, issuancesRecordData]);

  const restoreBondRecord = () =>
    bondRecordRecoveryRefetch().then(({ data }) => {
      setFirstLoading(false);

      if (!data || data?.product_id !== product?.id) {
        navigate(`?id=${product?.id}`);
        return;
      }
      const recoveryData = data && serializeRecoveryData(data?.principal);
      if (data) {
        if (data?.principal?.business_name) {
          setIsBusiness(true);
          setFormDataBondBusiness(recoveryData);
        } else {
          setIsBusiness(false);
          setFormDataBondApplicant(recoveryData);
        }
      }

      if (data?.status_name === BondStatuses.PAYMENT_REQUIRED) {
        setStep(Step.BOND_PREVIEW);
        return;
      }

      if (data?.workflow_status === WorkflowStatus.CUSTOMER) {
        // an external order is placed once and can't be edited, so going back
        // through its steps would only submit a second order
        if (isExternalProduct) {
          setStep(Step.PROCESSING);
        } else if (data?.principal?.business_name) {
          setStep(Step.BOND_BUSINESS);
        } else {
          setStep(Step.BOND_APPLICANT);
        }
        return;
      }

      if (data?.workflow_status === WorkflowStatus.PROCESSING) {
        setStep(Step.PROCESSING);
      }

      if (data?.workflow_status === WorkflowStatus.COMPLETE) {
        setStep(Step.COMPLETE);
      }
    });

  useEffect(() => {
    if (!queryAccessKey && !queryRecordId && product?.id) {
      setFirstLoading(false);

      if (hasInstructions) {
        setStep(Step.INSTRUCTIONS);
      }
    }

    if (queryAccessKey && queryRecordId && product?.id) {
      restoreBondRecord();
    }
  }, [product?.id]);

  // an external submission lands on the record it quoted, which then carries
  // on the way a returning applicant's does
  useEffect(() => {
    if (
      isExternalProduct &&
      step === Step.PROCESSING &&
      queryAccessKey &&
      queryRecordId
    ) {
      restoreBondRecord();
    }
  }, [queryAccessKey, queryRecordId]);

  useEffect(() => {
    if (isNeedToCheckIndemnitor && step === Step.BOND_INDEMNITOR) {
      refetch();
      setFirstFetchRecordLoading(false);
      setNeedToCheckIndemnitor(false);
    }
  }, [isNeedToCheckIndemnitor, step, refetch]);

  useEffect(() => {
    if (bondRecordRecoveryIsError) {
      setShowError(true);

      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  }, [bondRecordRecoveryIsError]);

  const isLoadingBondRecords =
    isLoading || isRefetching || bondRecordRecoveryIsRefetching;

  const isSkipBondIndemnitor =
    !isLoading && bondRecords && !bondRecords?.data.record?.is_credit_required;
  const isSkipBondOptions = false;

  const mailingState = isBusiness
    ? formDataBondBusiness?.mailing_state
    : formDataBondApplicant?.mailing_state;

  useEffect(() => {
    if (
      step === Step.BOND_INDEMNITOR &&
      !formDataBondIndemnitor &&
      !isSkipBondIndemnitor
    ) {
      setFormDataBondIndemnitor([{}]);
    }
  }, [step, formDataBondIndemnitor, isSkipBondIndemnitor]);

  const forms: FormItem[] = getForms({
    step,
    formDataStart,
    setFormDataStart,
    formDataBondApplicant,
    setFormDataBondApplicant,
    setFormDataBondBusiness,
    setIsBusiness,
    formDataBondBusiness,
    formDataBondRisk,
    setFormDataBondRisk,
    formDataBondSpecific,
    setFormDataBondSpecific,
    formDataBondOptions,
    setFormDataBondOptions,
    isSkipBondOptions,
    formDataBondAgencyQuestions,
    setFormDataBondAgencyQuestions,
    formDataExternalFormSpecific,
    setFormDataExternalFormSpecific,
    formDataExternalBondOptions,
    setFormDataExternalBondOptions
  });

  const isStart =
    step === Step.START ||
    step === Step.BOND_APPLICANT ||
    step === Step.BOND_BUSINESS;

  const steps: StepItem[] = isExternalProduct
    ? getExternalSteps({
        hasFormSpecificFields: hasExternalFormSpecificFields,
        step,
        setStep,
        isBusiness,
        formDataBondApplicant,
        formDataBondBusiness
      })
    : getSteps({
        formDataBondBusiness,
        formDataBondApplicant,
        setStep,
        setNeedToCheckIndemnitor,
        isStart,
        formDataBondRisk,
        formDataBondSpecific,
        isBusiness,
        step,
        formDataBondIndemnitor,
        formDataBondOptions,
        formDataBondAgencyQuestions,
        hasAgencyQuestions: !!context.state.bondAgencyQuestionsFormSchema
      });

  // only set on the external-only steps, so the principal steps keep reusing
  // the schemas fetched into BondAppFormContext
  const externalFormSchema = externalProduct
    ? getExternalSchemaForStep(step, externalProduct)
    : undefined;

  const currentFormSchema =
    externalFormSchema ||
    (activeSchema[step] &&
      getSchema(activeSchema, schema, step, context, mailingState));

  const externalPremiumInCents = externalProduct
    ? getExternalPremiumInCents(
        externalProduct,
        Number(formDataExternalBondOptions?.bond_amount) || undefined,
        Number(formDataExternalBondOptions?.term_in_years) || undefined
      )
    : null;

  const widgets = {
    CheckboxWidget: (props: any) =>
      CustomCheckbox(
        props,
        setFormDataBondBusiness,
        setFormDataBondApplicant,
        setFormDataStart,
        setStep,
      ),
    SelectWidget: (props: any) => CustomSelect(props),
    DateWidget: (props: any) => CustomDatePicker(props),
    SingleCheckboxWidget,
    CurrencyWidget,
  };

  const submitExternalOrder = async () => {
    if (!externalProduct) {
      return;
    }

    try {
      const { data: record } = await submitExternalBondRecord(
        getExternalBondRecordPayload({
          externalProduct,
          isBusiness,
          formDataBondApplicant,
          formDataBondBusiness,
          formDataExternalFormSpecific,
          formDataExternalBondOptions,
        }),
      );

      // submitting only quotes the bond; review, payment and delivery run on
      // the record it created, the same as any other application
      navigate(
        `?id=${product?.id}&recordId=${record.salesforce_id}&accessKey=${record.access_key}`,
      );
      setStep(Step.PROCESSING);
    } catch {
      // surfaced to the applicant through externalSubmitErrorMessage
    }
  };

  const submitExternalStep = () => {
    if (step === Step.BOND_APPLICANT || step === Step.BOND_BUSINESS) {
      setError(undefined);
      setStep(
        hasExternalFormSpecificFields
          ? Step.EXTERNAL_BOND_SPECIFIC
          : Step.EXTERNAL_BOND_OPTIONS,
      );
      return;
    }

    if (step === Step.EXTERNAL_BOND_SPECIFIC) {
      setStep(Step.EXTERNAL_BOND_OPTIONS);
      return;
    }

    if (step === Step.EXTERNAL_BOND_OPTIONS) {
      submitExternalOrder();
    }
  };

  const submit = () => {
    if (isExternalProduct) {
      submitExternalStep();
      return;
    }

    if (step === Step.BOND_APPLICANT || step === Step.BOND_BUSINESS) {
      setStep(Step.BOND_RISK);
      setError(undefined);

      setFirstFetchRecordLoading(true);
      refetch().finally(() => setNeedToCheckIndemnitor(true));
    }
    if (step === Step.BOND_RISK) {
      setStep(Step.BOND_SPECIFIC);
    }
    if (step === Step.BOND_SPECIFIC) {
      setError(undefined);
      if (!!context.state.bondAgencyQuestionsFormSchema) {
        setStep(Step.BOND_AGENCY_QUESTIONS);
        return;
      }
      refetch().then(({ data }) => {
        if (data?.data?.record?.is_credit_required) {
          setStep(Step.BOND_INDEMNITOR);
        } else {
          setStep(Step.BOND_OPTIONS);
        }
      });
    }
    if (step === Step.BOND_INDEMNITOR) {
      setStep(Step.BOND_OPTIONS);
    }

    if (step === Step.BOND_OPTIONS) {
      refetch().then(({ data }) => {
        if (data?.data?.record?.status_name === BondStatuses.PAYMENT_REQUIRED) {
          setStep(Step.BOND_PREVIEW);
        } else {
          setStep(Step.PROCESSING);
        }
      });
    }

    if ((step === Step.BOND_SPECIFIC) && !!context.state.bondAgencyQuestionsFormSchema) {
      setStep(Step.BOND_AGENCY_QUESTIONS);
    }

    if (step === Step.BOND_AGENCY_QUESTIONS) {
      refetch().then(({ data }) => {
        if (data?.data?.record?.is_credit_required) {
          setStep(Step.BOND_INDEMNITOR);
        } else {
          setStep(Step.BOND_OPTIONS);
        }
      });
    }
  };

  const getLefts = (id: number) =>
    currentFormSchema?.required?.filter(
      (field: string) =>
        !formDataBondIndemnitor?.[id]?.[field] &&
        formDataBondIndemnitor?.[id]?.[field] !== false,
    )?.length;

  const handleDeliverySubmit = async ({ id, ...rest }: any) => {
    try {
      const shippingPayload = {
        ...rest,
        record_access_key: queryAccessKey,
      };
      setIsErrorSaveShipping(false);
      setIsShippingLoading(true);
      await ShippingService.saveShippingOptions(
        queryRecordId,
        shippingPayload,
        id,
      );

      await bondRecordRecoveryRefetch();
      await fetchShippingOptions();
      window.location.href = paymentLink.toString();
    } catch (err) {
      setIsErrorSaveShipping(true);
    }
  };

  const isLoadingBondIndemnitor =
    step === Step.BOND_INDEMNITOR &&
    (isLoadingBondRecords || isFirstFetchRecordLoading);

  const isBondEnabled =
    (activeSchema[step] &&
      Object.keys(currentFormSchema?.properties || {}).length &&
      step !== Step.BOND_INDEMNITOR &&
      step !== Step.PROCESSING) ||
    (step === Step.BOND_INDEMNITOR && !isSkipBondIndemnitor) ||
    (step === Step.PROCESSING && !isSkipBondOptions);

  const isBlocked =
    currentBondRecordData?.workflow_status === WorkflowStatus.VOID;

  const externalBondInfo = isExternalProduct
    ? getExternalBondInfo({
        step,
        premiumInCents: externalPremiumInCents,
        mailingState,
      })
    : undefined;

  const bondInfo: BondInfo =
    externalBondInfo ||
    getBondInfo({
      step,
      bondName: product?.base_name,
      isEnabled: isBondEnabled,
      mailingState,
      bondStatus: currentBondRecordData?.status_name || bondStatus,
      bondData: currentBondRecordData,
      isSkipBondIndemnitor,
      isBlocked,
      isOfflineApplication: product?.offline_application,
    });

  const pdfID =
    issuancesRecordData?.pdf_bond_sample_file_id ||
    bondRecoveryData?.status_name === BondStatuses.BOND_ISSUED
      ? bondRecoveryData?.pdf_electronic_bond_file_id
      : bondRecoveryData?.pdf_bond_sample_file_id ||
        bondRecordCurrentData?.pdf_bond_sample_file_id;
  const pdfUrl =
    !issuancesRecordData &&
    !pdfElectronicBondFileId &&
    (bondRecordCurrentData?.id || bondRecoveryData?.id) &&
    (bondRecordCurrentData?.pdf_bond_sample_file_id ||
      bondRecoveryData?.pdf_bond_sample_file_id) &&
    (queryAccessKey ||
      bondRecord?.data?.access_key ||
      (Array.isArray(bondRecoveryData?.adjustments_records) &&
        bondRecoveryData?.adjustments_records[0]?.auth_access_key))
      ? getPdfUrl({
          bondId: bondRecordCurrentData?.id || bondRecoveryData?.id,
          pdfId: pdfID,
          bondAccessKey:
            queryAccessKey ||
            bondRecord?.data?.access_key ||
            bondRecoveryData?.adjustments_records[0]?.auth_access_key,
        })
      : (issuancesRecordData?.status_name === BondStatuses.BOND_ISSUED ||
          bondStatus === BondStatuses.BOND_ISSUED) &&
        queryAccessKey
      ? getPdfUrl({
          bondId: issuancesRecordData?.id || bondRecoveryData?.id,
          pdfId:
            issuancesRecordData?.pdf_electronic_bond_file_id ||
            pdfElectronicBondFileId,
          bondAccessKey: queryAccessKey,
        })
      : "";

  const currentStep = isExternalProduct
    ? getExternalStepHeader(step, hasExternalFormSpecificFields)
    // the standard mapping covers every step outside the external flow
    : stepWidthMapping[step as keyof typeof stepWidthMapping];

  const sidebarData: SidebarData = portalSiteInfo
    ? {
        name: portalSiteInfo.gig_agency_name,
        description: portalSiteInfo?.description,
        location: portalSiteInfo?.location,
        phone: portalSiteInfo?.phone_number,
        partnershipName: portalSiteInfo.partnership_name,
      }
    : partners?.[partnerId as keyof typeof partners] ?? partners?.default;

  const creditContext: FormContextType = {
    step,
    setStep,
    formDataBondIndemnitor,
    setFormDataBondIndemnitor,
    isFirstLoading,
    isShippingLoading,
    isErrorSaveShipping,
    queryPaymentStatus,
    queryRecordId,
    queryAccessKey,
    bondRecordCurrentData,
    bondRecoveryData,
    bondRecordRecoveryIsLoading,
    bondRecordRecoveryIsError,
    shippingOptions,
    isError,
    issuancesRecordData,
    issuancesIsFetching,
    shippingMethods,
    paymentLink,
    product,
    isBondLoading,
    isBondError,
    isLoadingBondRecords,
    isSkipBondIndemnitor,
    appRef,
    forms,
    steps,
    submit,
    widgets,
    currentFormSchema,
    bondInfo,
    isLoadingBondIndemnitor,
    pdfUrl,
    handleDeliverySubmit,
    bondRecordRecoveryError,
    getLefts,
    currentStep,
    sidebarData,
    isBusiness,
    error,
    setAmountOfIndemnitor,
    amountOfIndemnitor,
    bondRecord,
    isShowError,
    currentBondRecordData,
    isBlocked,
    mailingState,
    fetchShippingMethods,
    fetchShippingOptions,
    isExternalProduct,
    externalProduct,
    isExternalSubmitLoading,
    externalSubmitErrorMessage,
  };

  return (
    <FormContext.Provider value={creditContext}>
      {children}
    </FormContext.Provider>
  );
};
